import { useEffect, useState } from "react"
import { HocuspocusProvider } from "@hocuspocus/provider"
import * as Y from "yjs"

import type { Content } from "@tiptap/core"
import { env } from "@/env"
import { isSeeded, isSessionLeader, wasSavedBySession } from "./shared-doc"

// Past this the editor opens on its own instead of waiting on the live server.
const CONNECT_TIMEOUT_MS = 3_000

export type SharedDoc =
	| { status: "connecting" }
	| { status: "offline" }
	| { status: "ready"; ydoc: Y.Doc; provider: HocuspocusProvider }

/**
 * Joins the live shared document `name`. `offline` means edit without it, the
 * way the editor always worked; a session that drops later reconnects on its own
 * and keeps every local edit.
 */
export function useSharedDoc({
	name,
	token,
	enabled,
}: {
	name: string
	token: string | undefined
	enabled: boolean
}): SharedDoc {
	const [state, setState] = useState<SharedDoc>({ status: "connecting" })

	useEffect(() => {
		if (!enabled || !token) {
			setState({ status: "offline" })
			return
		}
		setState({ status: "connecting" })
		const ydoc = new Y.Doc()
		const provider = new HocuspocusProvider({
			url: `${env.VITE_LIVE_URL}/hp/docs/${encodeURIComponent(name)}`,
			name,
			document: ydoc,
			token,
		})
		let settled = false
		const timer = setTimeout(() => {
			if (settled) return
			settled = true
			provider.destroy()
			setState({ status: "offline" })
		}, CONNECT_TIMEOUT_MS)
		const onSynced = () => {
			if (settled) return
			settled = true
			clearTimeout(timer)
			setState({ status: "ready", ydoc, provider })
		}
		provider.on("synced", onSynced)
		provider.on("authenticationFailed", () => {
			if (settled) return
			settled = true
			clearTimeout(timer)
			provider.destroy()
			setState({ status: "offline" })
		})

		return () => {
			clearTimeout(timer)
			provider.off("synced", onSynced)
			provider.destroy()
			ydoc.destroy()
		}
	}, [name, token, enabled])

	return state
}

/**
 * Whether content written outside the editor (an AI generation, a translation,
 * a restore, an API edit) should be pushed into it. In a live session a save by
 * any editor is already in the doc, and only one editor applies the rest.
 */
export function acceptsOutsideEdit(
	sharedDoc: SharedDoc,
	content: Content,
): boolean {
	if (sharedDoc.status !== "ready") return true
	// Before the seed the push would add a second copy of the body; the seed brings the newest row anyway.
	if (!isSeeded(sharedDoc.ydoc)) return false
	if (wasSavedBySession(sharedDoc.ydoc, content)) return false
	const connected = sharedDoc.provider.awareness?.getStates().keys() ?? []
	return isSessionLeader(sharedDoc.ydoc, connected)
}
